import { EnvironmentConfig } from '@me8eon/config'
import type { ChatRuntimeConfig } from '../types/chat'
import { featureFlags } from './features.config'


const DEFAULT_AGENT_ENDPOINT = `${EnvironmentConfig.aiServer}/agent`
const DEFAULT_CONVERSATIONS_ENDPOINT = `${EnvironmentConfig.aiServer}/threads`
const DEFAULT_MCP_PROXY_ENDPOINT = `${EnvironmentConfig.aiServer}/mcp/proxy`
const DEFAULT_MCP_SANDBOX_URL = '/sandbox-proxy.html'
const DEFAULT_MAX_EVENT_RECORDS = 80

export const MCP_APPS_ACTIVITY_TYPE = 'mcp-apps'
export const TURN_METADATA_ACTIVITY_TYPE = 'turn-metadata'

/**
 * Dispatched on `window` whenever a run finishes successfully — the signal
 * a persisted thread may have just been created or had a new message added.
 * `RecentChats` lives in the app-wide sidebar, outside the `/new-ai` page's
 * own `ChatProvider`, so it can't read `state.isRunning` directly; this
 * decoupled event lets it refresh without either side needing to know about
 * the other.
 */
export const CONVERSATION_UPDATED_EVENT = 'me8eon:new-ai:conversation-updated'

/**
 * Dispatched on `window` whenever a run starts/finishes for the active
 * thread — lets the (decoupled) sidebar disable deleting a thread while a
 * response is still streaming for it, without depending on `ChatProvider`.
 * `detail: { isRunning: boolean }`.
 */
export const CONVERSATION_RUN_STATE_EVENT = 'me8eon:new-ai:conversation-run-state'

/**
 * Asks the `/new-ai` Activities panel to open from outside its `ChatProvider`.
 * A pending flag is also latched so a panel that mounts after navigation still opens.
 */
export const ACTIVITIES_PANEL_OPEN_EVENT = 'me8eon:new-ai:activities-panel-open'

let pendingActivitiesPanelOpen = false

export function requestActivitiesPanelOpen(): void {
  pendingActivitiesPanelOpen = true
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ACTIVITIES_PANEL_OPEN_EVENT))
  }
}

/** Reads and clears the pending flag set by `requestActivitiesPanelOpen`. */
export function consumePendingActivitiesPanelOpen(): boolean {
  const wasPending = pendingActivitiesPanelOpen
  pendingActivitiesPanelOpen = false
  return wasPending
}

export const defaultChatConfig: ChatRuntimeConfig = {
    agentEndpoint: DEFAULT_AGENT_ENDPOINT,
    aiServerBase: EnvironmentConfig.aiServer,
    conversationsEndpoint: DEFAULT_CONVERSATIONS_ENDPOINT,
    debugEnabled: featureFlags.chatDebug,
    mcpProxyEndpoint: DEFAULT_MCP_PROXY_ENDPOINT,
    mcpSandboxUrl: DEFAULT_MCP_SANDBOX_URL,
    maxEventRecords: DEFAULT_MAX_EVENT_RECORDS,
}
